import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteField,
  Unsubscribe
} from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import { db, auth } from './firebase';
import { EmailCustomTemplate } from '../types';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';

export const EMAIL_SETTINGS_DOC = 'email_settings/config';

export interface TemplateDefinition {
  id: string;
  name: string;
  triggerDescription: string;
  badgeColor: string;
  defaultSubject: string;
  defaultBody: string;
  supportedFields: {
    key: string;
    label: string;
    description: string;
    isEssential?: boolean; // Cannot be removed if true
  }[];
  sampleData: Record<string, string>;
}

export const SUPPORTED_TEMPLATES: TemplateDefinition[] = [
  {
    id: 'reservation_created',
    name: 'Reservation Confirmation',
    triggerDescription: 'Sent automatically when a parent submits an order online.',
    badgeColor: 'bg-sky-100 text-sky-800 border-sky-200',
    defaultSubject: 'Your Uniform Loop Reservation - {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

Thank you for using Uniform Loop! We have successfully received your reservation request for pre-loved school uniforms.

Customer Reference: {REFERENCE_CODE}

{RESERVATION_DETAILS}

📍 Collection Information
Collection Location: {COLLECTION_VENUE}
Address: {COLLECTION_ADDRESS}
Chosen Collection Date: {COLLECTION_DATE}
Hub Opening Times: {OPENING_HOURS}

🛠️ Find & Manage Your Reservation
Need to change your collection date, update details, leave a note for volunteers, or cancel?
Use your Reference {REFERENCE_CODE} and email address {CUSTOMER_EMAIL} on our website to find and manage your reservation.

Cancellation & Changes: If you no longer require these items or cannot make your collection slot, please visit Find My Reservation or reply to this email so we can return the items to stock for other families.`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: "Parent or carer's full name" },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Unique reservation reference code', isEssential: true },
      { key: '{CUSTOMER_EMAIL}', label: 'Customer Email', description: "Parent's contact email address" },
      { key: '{COLLECTION_DATE}', label: 'Collection Date', description: 'Booked collection day or date' },
      { key: '{COLLECTION_VENUE}', label: 'Collection Venue', description: 'Community hub name (e.g. Bert Boyden Centre)' },
      { key: '{COLLECTION_ADDRESS}', label: 'Collection Address', description: 'Physical pickup address' },
      { key: '{OPENING_HOURS}', label: 'Opening Hours', description: 'Opening times of the collection venue' },
      { key: '{RESERVATION_DETAILS}', label: 'Reservation Details', description: 'Table of reserved items, sizes, and quantities' }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'Sarah Jenkins',
      '{REFERENCE_CODE}': 'UL-RES-20260815-408921',
      '{CUSTOMER_EMAIL}': 'sarah.jenkins@example.co.uk',
      '{COLLECTION_DATE}': 'Thursday 15 August 2026',
      '{COLLECTION_VENUE}': 'Bert Boyden Community Centre, Immingham',
      '{COLLECTION_ADDRESS}': '34 Carver Road, Immingham DN40 1DS',
      '{OPENING_HOURS}': 'Tue, Thu, Sat 10:00 - 14:00',
      '{RESERVATION_DETAILS}': `Reserved Items:
• 2x Immingham Oasis Academy Blazer (Size 32)
• 3x White Polo Shirt (Size 9-10 Yrs)
• 2x Black School Trousers (Size 9-10 Yrs)`
    }
  },
  {
    id: 'order_prepared',
    name: 'Order Being Prepared',
    triggerDescription: 'Sent when volunteers begin gathering and inspecting requested items.',
    badgeColor: 'bg-blue-100 text-blue-800 border-blue-200',
    defaultSubject: 'Your Uniform Order Is Being Prepared - {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

Our volunteers are currently locating and bagging your requested uniform items at the community hub.

Customer Reference: {REFERENCE_CODE}

📍 Collection Details:
Collection Date: {COLLECTION_DATE}
Hub Location: {COLLECTION_VENUE}
Address: {COLLECTION_ADDRESS}

You will receive another notification as soon as your items are packed and ready for collection!`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: "Parent or carer's full name" },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Unique reservation reference code', isEssential: true },
      { key: '{COLLECTION_DATE}', label: 'Collection Date', description: 'Booked collection day' },
      { key: '{COLLECTION_VENUE}', label: 'Collection Venue', description: 'Community hub name' },
      { key: '{COLLECTION_ADDRESS}', label: 'Collection Address', description: 'Physical pickup address' }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'Sarah Jenkins',
      '{REFERENCE_CODE}': 'UL-RES-20260815-408921',
      '{COLLECTION_DATE}': 'Thursday 15 August 2026',
      '{COLLECTION_VENUE}': 'Bert Boyden Community Centre, Immingham',
      '{COLLECTION_ADDRESS}': '34 Carver Road, Immingham DN40 1DS'
    }
  },
  {
    id: 'ready_for_collection',
    name: 'Ready for Collection',
    triggerDescription: 'Sent when items are packed and waiting at Bert Boyden Centre.',
    badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    defaultSubject: 'Your Uniforms Are Ready for Collection: {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

Great news! Our volunteers have packed and prepared your reserved uniform items.

Customer Reference: {REFERENCE_CODE}

📍 Collection Point Details:
Hub Location: {COLLECTION_VENUE}
Address: {COLLECTION_ADDRESS}
Opening Hours: {OPENING_HOURS}

Instructions: Simply state your reference ({REFERENCE_CODE}) or show this email to a volunteer when you arrive.`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: "Parent or carer's full name" },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Unique reservation reference code', isEssential: true },
      { key: '{COLLECTION_VENUE}', label: 'Collection Venue', description: 'Community hub name' },
      { key: '{COLLECTION_ADDRESS}', label: 'Collection Address', description: 'Physical pickup address' },
      { key: '{OPENING_HOURS}', label: 'Opening Hours', description: 'Collection opening hours' }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'Sarah Jenkins',
      '{REFERENCE_CODE}': 'UL-RES-20260815-408921',
      '{COLLECTION_VENUE}': 'Bert Boyden Community Centre, Immingham',
      '{COLLECTION_ADDRESS}': '34 Carver Road, Immingham DN40 1DS',
      '{OPENING_HOURS}': 'Tue, Thu, Sat 10:00 - 14:00'
    }
  },
  {
    id: 'reservation_cancelled',
    name: 'Reservation Cancelled',
    triggerDescription: 'Sent if an order is cancelled; items are restored to catalogue stock.',
    badgeColor: 'bg-rose-100 text-rose-800 border-rose-200',
    defaultSubject: 'Reservation Cancelled - {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

This email confirms that your reservation {REFERENCE_CODE} has been cancelled.

All items from this reservation have been safely returned to our active stock catalogue so other families in the community can access them.

If you still require school uniforms, please visit our website to place a new reservation whenever you are ready.`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: "Parent or carer's full name" },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Unique reservation reference code', isEssential: true }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'Sarah Jenkins',
      '{REFERENCE_CODE}': 'UL-RES-20260815-408921'
    }
  },
  {
    id: 'collection_date_updated',
    name: 'Collection Date Decision',
    triggerDescription: 'Sent when a requested date change is approved or rescheduled.',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
    defaultSubject: 'Collection Date Request {DECISION} - {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

Your request to change the collection date for reservation {REFERENCE_CODE} has been {DECISION_LOWER} by our volunteer team.

New Collection Date: {COLLECTION_DATE}
{VOLUNTEER_NOTE}

You can view and manage your reservation at any time on the Find My Reservation page using your reference {REFERENCE_CODE} and email address.`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: "Parent or carer's full name" },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Unique reservation reference code', isEssential: true },
      { key: '{COLLECTION_DATE}', label: 'Collection Date', description: 'Approved or updated collection date' },
      { key: '{DECISION}', label: 'Decision', description: 'Approved or Rejected' },
      { key: '{DECISION_LOWER}', label: 'Decision (lowercase)', description: 'approved or rejected' },
      { key: '{VOLUNTEER_NOTE}', label: 'Volunteer Note', description: 'Optional explanation note from the volunteer' }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'Sarah Jenkins',
      '{REFERENCE_CODE}': 'UL-RES-20260815-408921',
      '{COLLECTION_DATE}': 'Saturday 17 August 2026',
      '{DECISION}': 'Approved',
      '{DECISION_LOWER}': 'approved',
      '{VOLUNTEER_NOTE}': 'Volunteer Note: "We have moved your reservation to Saturday morning."'
    }
  },
  {
    id: 'volunteer_reply',
    name: 'Volunteer Reply',
    triggerDescription: 'Sent when a volunteer replies to a family enquiry from Customer Messages.',
    badgeColor: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    defaultSubject: 'Re: Uniform Loop Enquiry {REFERENCE_CODE}',
    defaultBody: `Dear {CUSTOMER_NAME},

{MESSAGE_CONTENT}

Sent by the Uniform Loop Volunteer Team ({VOLUNTEER_NAME}).`,
    supportedFields: [
      { key: '{CUSTOMER_NAME}', label: 'Customer Name', description: 'Name of the recipient parent or family member' },
      { key: '{REFERENCE_CODE}', label: 'Reference Code', description: 'Message enquiry reference number', isEssential: true },
      { key: '{VOLUNTEER_NAME}', label: 'Volunteer Name', description: 'Name of volunteer replying' },
      { key: '{MESSAGE_CONTENT}', label: 'Message Content', description: 'The custom reply body composed by the volunteer', isEssential: true }
    ],
    sampleData: {
      '{CUSTOMER_NAME}': 'David Miller',
      '{REFERENCE_CODE}': 'UL-MSG-20260815-719302',
      '{VOLUNTEER_NAME}': 'Emma Wilson',
      '{MESSAGE_CONTENT}': 'Hello David, thank you for reaching out. Yes, we have received additional blazers in size 34 that will be available at Thursday’s hub session.'
    }
  }
];

/**
 * Subscribe to all customized email templates from Firestore.
 * Safely coordinates with Firebase Auth so that listeners are only established when
 * an authenticated session is active, avoiding premature permission-denied errors on startup/refresh.
 */
export function subscribeCustomTemplates(
  onData: (templates: Record<string, EmailCustomTemplate>) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  let unsubSnapshot: Unsubscribe | null = null;
  let isSubscribed = true;
  let activeUserId: string | null = null;

  const startFirestoreListener = () => {
    if (!isSubscribed) return;
    if (unsubSnapshot) {
      unsubSnapshot();
      unsubSnapshot = null;
    }
    try {
      const docRef = doc(db, EMAIL_SETTINGS_DOC);
      unsubSnapshot = onSnapshot(
        docRef,
        (docSnap) => {
          if (!isSubscribed) return;
          const result: Record<string, EmailCustomTemplate> = {};
          if (docSnap && docSnap.exists()) {
            const data = docSnap.data();
            const rawTemplates = data?.templates;
            if (rawTemplates && typeof rawTemplates === 'object') {
              for (const [tplId, tplData] of Object.entries(rawTemplates)) {
                if (tplData && typeof tplData === 'object') {
                  result[tplId] = {
                    ...(tplData as EmailCustomTemplate),
                    templateId: tplId
                  };
                }
              }
            }
          }
          onData(result);
        },
        (err) => {
          if (!isSubscribed) return;
          console.warn('Note: Email custom templates listener error:', err?.message || err);
          onData({});
          if (onError) onError(err);
        }
      );
    } catch (err: any) {
      if (!isSubscribed) return;
      console.warn('Failed to initialize custom templates listener:', err);
      onData({});
      if (onError) onError(err);
    }
  };

  // If already authenticated, attach listener immediately
  if (auth.currentUser) {
    activeUserId = auth.currentUser.uid;
    startFirestoreListener();
  }

  // Subscribe to auth state transitions to establish or tear down Firestore listener safely
  const unsubAuth = onAuthStateChanged(auth, (user: User | null) => {
    if (!isSubscribed) return;
    if (user) {
      if (user.uid !== activeUserId || !unsubSnapshot) {
        activeUserId = user.uid;
        startFirestoreListener();
      }
    } else {
      activeUserId = null;
      if (unsubSnapshot) {
        unsubSnapshot();
        unsubSnapshot = null;
      }
      onData({});
    }
  });

  return () => {
    isSubscribed = false;
    unsubAuth();
    if (unsubSnapshot) {
      unsubSnapshot();
      unsubSnapshot = null;
    }
  };
}

/**
 * Fetch a single custom template from Firestore, or null if using default
 */
export async function fetchCustomTemplate(templateId: string): Promise<EmailCustomTemplate | null> {
  if (!templateId) return null;
  // If not signed in, unauthenticated callers (e.g. public reservation checkout) safely receive null to use built-in system defaults
  if (!auth.currentUser) {
    return null;
  }
  try {
    const docRef = doc(db, EMAIL_SETTINGS_DOC);
    const snap = await getDoc(docRef);
    if (snap && snap.exists()) {
      const data = snap.data();
      const rawTemplates = data?.templates;
      if (rawTemplates && typeof rawTemplates === 'object' && rawTemplates[templateId]) {
        const tplData = rawTemplates[templateId];
        if (tplData && typeof tplData === 'object') {
          return { ...(tplData as EmailCustomTemplate), templateId };
        }
      }
    }
    return null;
  } catch (err) {
    console.warn(`Failed to fetch custom template ${templateId}:`, err);
    return null;
  }
}

export const ALL_SUPPORTED_TOKENS = Array.from(
  new Set(
    SUPPORTED_TEMPLATES.flatMap((t) => t.supportedFields.map((f) => f.key))
  )
);

export interface TemplateValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates that template text adheres to required tokens and supported variable specifications
 */
export function validateTemplateTokens(
  templateId: string,
  subject?: string | null,
  body?: string | null
): TemplateValidationResult {
  const errors: string[] = [];
  const safeSubject = subject || '';
  const safeBody = body || '';

  const tplDef = SUPPORTED_TEMPLATES.find((t) => t.id === templateId);
  if (!tplDef) {
    return { isValid: false, errors: [`Unrecognized template type: ${templateId}`] };
  }

  const allowedKeysForTemplate = new Set(tplDef.supportedFields.map((f) => f.key));

  // 1. Check for required system tokens
  const essentialFields = tplDef.supportedFields.filter((f) => f.isEssential);
  for (const field of essentialFields) {
    const presentInSubject = safeSubject.includes(field.key);
    const presentInBody = safeBody.includes(field.key);
    if (!presentInSubject && !presentInBody) {
      errors.push(`Missing required token: ${field.key} (${field.label})`);
    }
  }

  // Always ensure critical {REFERENCE_CODE} is present if required by this template
  if (tplDef.supportedFields.some((f) => f.key === '{REFERENCE_CODE}' && f.isEssential)) {
    if (!safeSubject.includes('{REFERENCE_CODE}') && !safeBody.includes('{REFERENCE_CODE}')) {
      if (!errors.some((e) => e.includes('{REFERENCE_CODE}'))) {
        errors.push('Missing required token: {REFERENCE_CODE} (Reference Code)');
      }
    }
  }

  // 2. Scan both subject and body for tokens of format {VARIABLE_NAME} and catch malformed or unknown tokens
  const bracketPattern = /\{([^}]+)\}/g;
  const combinedText = `${safeSubject}\n${safeBody}`;
  let match: RegExpExecArray | null;
  const seenPatterns = new Set<string>();

  while ((match = bracketPattern.exec(combinedText)) !== null) {
    const rawToken = match[0];
    const inner = match[1];
    if (seenPatterns.has(rawToken)) continue;
    seenPatterns.add(rawToken);

    // If recognized and allowed for this template, it is valid
    if (allowedKeysForTemplate.has(rawToken)) {
      continue;
    }

    // Check if it's a known token from another template
    if (ALL_SUPPORTED_TOKENS.includes(rawToken)) {
      errors.push(`The variable token ${rawToken} is not applicable to this template (${tplDef.name}).`);
      continue;
    }

    // Detect malformed tokens: spaces, lowercase characters, special symbols
    if (!/^[A-Z0-9_]+$/.test(inner)) {
      errors.push(
        `Malformed variable token detected: "${rawToken}". Tokens must use uppercase letters and underscores without spaces (e.g. {REFERENCE_CODE}).`
      );
    } else {
      errors.push(`Unrecognized or unsupported variable token: "${rawToken}".`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Save or update a customized email template in Firestore
 */
export async function saveCustomTemplate(
  templateId: string,
  subject: string,
  body: string,
  userDisplayName?: string
): Promise<void> {
  const safeSubject = (subject || '').trim();
  const safeBody = (body || '').trim();

  const validation = validateTemplateTokens(templateId, safeSubject, safeBody);
  if (!validation.isValid) {
    throw new Error(`Template validation failed: ${validation.errors.join('; ')}`);
  }

  const docRef = doc(db, EMAIL_SETTINGS_DOC);
  const templateData: EmailCustomTemplate = {
    templateId,
    subject: safeSubject,
    body: safeBody,
    updatedAt: new Date().toISOString(),
    updatedBy: userDisplayName || 'Administrator',
    active: true
  };

  // Safe targeted write: use setDoc with merge: true on the templates map
  // This updates templates[templateId] while preserving all existing SMTP settings and other templates.
  await setDoc(
    docRef,
    sanitizeFirestoreData({
      templates: {
        [templateId]: templateData
      }
    }),
    { merge: true }
  );
}

/**
 * Reset a customized template to the system default by removing it from Firestore
 */
export async function resetCustomTemplateToDefault(templateId: string): Promise<void> {
  const docRef = doc(db, EMAIL_SETTINGS_DOC);
  // Uses deleteField on the specific nested template key
  // This removes templates[templateId] while leaving all other templates and all SMTP fields untouched
  await updateDoc(docRef, {
    [`templates.${templateId}`]: deleteField()
  });
}

/**
 * Replaces dynamic placeholders {KEY} with actual values safely
 */
export function interpolateVariables(
  templateString?: string | null,
  variables?: Record<string, string> | null
): string {
  if (!templateString || typeof templateString !== 'string') {
    return '';
  }
  if (!variables || typeof variables !== 'object') {
    return templateString;
  }
  let result = templateString;
  try {
    const entries = Object.entries(variables);
    for (const [key, value] of entries) {
      if (key && typeof key === 'string') {
        result = result.split(key).join(value != null ? String(value) : '');
      }
    }
  } catch (err) {
    console.warn('Error during variable interpolation:', err);
  }
  return result;
}

/**
 * Converts a plain-text/multiline customized template body into clean branded HTML
 */
export function convertBodyToHtml(bodyText?: string | null): string {
  if (!bodyText || typeof bodyText !== 'string') {
    return '';
  }
  // Split on double line breaks to form paragraphs, preserving single line breaks
  const paragraphs = bodyText.split(/\n\s*\n/);
  return paragraphs
    .map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return '';
      // Check if this looks like a header or section label
      if (trimmed.startsWith('📍') || trimmed.startsWith('🛠️') || trimmed.startsWith('📦') || trimmed.startsWith('🎉')) {
        const lines = trimmed.split('\n');
        const title = lines[0];
        const rest = lines.slice(1).join('<br/>');
        return `
          <div class="card" style="margin-top: 16px;">
            <h3 style="margin-top:0; font-size: 14px; color: #002366; font-weight: 700;">${title}</h3>
            ${rest ? `<p style="margin: 4px 0; font-size: 13px; color: #334155; line-height: 1.6;">${rest}</p>` : ''}
          </div>
        `;
      }
      return `<p style="margin: 8px 0; font-size: 14px; line-height: 1.6; color: #1e293b;">${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('');
}

